import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button/button';
import { Sparkles, Copy, Check, X, ArrowLeft, Edit3, Save, RotateCcw } from 'lucide-react';
import { VideoAnalysisService, VideoAnalysisTriggerRequest } from 'src/services/video-analysis/video-analysis.service';
import { useToast } from 'src/components/ui/use-toast';
import { Logger } from 'src/utils/logger';

interface ContentDisplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  contentType: VideoAnalysisTriggerRequest['request_type'] | null;
  onBack?: () => void;
  isEditMode?: boolean;
  onSave?: (editedContent: string) => Promise<boolean>;
  onRegenerate?: () => void;
  showRegenerateOption?: boolean;
  isSaving?: boolean;
}

export const ContentDisplayModal: React.FC<ContentDisplayModalProps> = ({
  isOpen,
  onClose,
  content,
  contentType,
  onBack,
  isEditMode = false,
  onSave,
  onRegenerate,
  showRegenerateOption = false,
  isSaving = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(isEditMode);
  const [editedContent, setEditedContent] = useState(content);
  const { toast } = useToast();

  // Update edited content when content prop changes
  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  // Reset editing state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsEditing(isEditMode);
      setEditedContent(content);
    } else {
      setIsEditing(false);
      setCopied(false);
    }
  }, [isOpen, isEditMode, content]);

  // Copy to clipboard with fallback
  const copyToClipboard = async () => {
    const textToCopy = isEditing ? editedContent : content;
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        description: 'Content copied to clipboard!',
        variant: 'success',
      });
    } catch (error) {
      // Fallback to older method for user activation issues
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({
          description: 'Content copied to clipboard!',
          variant: 'success',
        });
      } catch (fallbackError) {
        Logger.error('Failed to copy to clipboard', 'ContentDisplayModal', error);
        toast({
          description: 'Failed to copy to clipboard',
          variant: 'destructive',
        });
      }
    }
  };

  // Handle save action
  const handleSave = async () => {
    if (!onSave || !editedContent.trim()) return;

    const success = await onSave(editedContent);
    if (success) {
      setIsEditing(false);
      toast({
        description: 'Content saved successfully!',
        variant: 'success',
      });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedContent(content);
    setIsEditing(false);
  };

  // Handle regenerate
  const handleRegenerate = () => {
    onRegenerate?.();
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  const handleBack = () => {
    setCopied(false);
    onBack?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="tw-flex tw-max-h-[90vh] tw-max-w-2xl tw-flex-col">
        <DialogHeader className="tw-flex-shrink-0">
          <DialogTitle className="tw-flex tw-items-center tw-justify-between tw-pr-8">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Sparkles className="tw-h-5 tw-w-5 tw-text-purple-600" />
              <span>Generated Content</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          <div className="tw-relative tw-space-y-4">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="tw-absolute tw-left-0 tw-top-1 tw-z-10 tw-h-8 tw-w-8 tw-rounded-full tw-p-0"
              >
                <ArrowLeft className="tw-h-4 tw-w-4" />
              </Button>
            )}
            <div className={onBack ? "tw-pl-10" : ""}>
              <div className="tw-flex tw-items-center tw-justify-between">
                <h3 className="tw-font-semibold tw-text-purple-600">
                  {isEditing ? 'Edit' : 'Generated'} {contentType ? VideoAnalysisService.getRequestTypeLabel(contentType) : 'Content'}
                </h3>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {showRegenerateOption && !isEditing && (
                    <Button
                      onClick={handleRegenerate}
                      size="sm"
                      variant="outline"
                      className="tw-gap-2"
                    >
                      <RotateCcw className="tw-h-4 tw-w-4" />
                      Regenerate
                    </Button>
                  )}
                  {!isEditing && onSave && (
                    <Button
                      onClick={() => setIsEditing(true)}
                      size="sm"
                      variant="outline"
                      className="tw-gap-2"
                    >
                      <Edit3 className="tw-h-4 tw-w-4" />
                      Edit
                    </Button>
                  )}
                  <Button
                    onClick={copyToClipboard}
                    size="sm"
                    variant="outline"
                    className="tw-gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="tw-h-4 tw-w-4 tw-text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="tw-h-4 tw-w-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="tw-mt-4 tw-max-h-80 tw-overflow-y-auto tw-rounded-lg tw-border tw-bg-gray-50 tw-p-4">
                {isEditing ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="tw-w-full tw-min-h-[200px] tw-resize-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-leading-relaxed tw-outline-none focus:tw-ring-0"
                    placeholder="Enter your content here..."
                  />
                ) : (
                  <pre className="tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed">
                    {content}
                  </pre>
                )}
              </div>

              {isEditing && (
                <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
                  <Button
                    onClick={handleCancelEdit}
                    size="sm"
                    variant="outline"
                    disabled={isSaving}
                    className="tw-h-8 tw-w-8 tw-p-0"
                  >
                    <X className="tw-h-4 tw-w-4" />
                  </Button>
                  <Button
                    onClick={handleSave}
                    size="sm"
                    disabled={isSaving || !editedContent.trim()}
                    className="tw-h-8 tw-w-8 tw-p-0 tw-bg-green-50 tw-border-green-300 tw-text-green-600 hover:tw-bg-green-100 hover:tw-border-green-400"
                  >
                    {isSaving ? (
                      <div className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-green-600 tw-border-t-transparent" />
                    ) : (
                      <Check className="tw-h-4 tw-w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed button area at bottom */}
        <div className="tw-mt-4 tw-flex-shrink-0 tw-border-t tw-pt-4">
          <div className="tw-flex tw-justify-end">
            <Button onClick={handleClose} variant="default">
              Done
            </Button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};
